import { matchPath } from "react-router";
import { routesMap, typedUrlForRoute } from "$root/components/PlatformRouter/RoutesDef";
import { AS_OF_SEARCH_PARAM, PROPOSAL_SEARCH_PARAM, parseAsOfParam } from "$root/pages/PortfolioDetails/as-of/asOf";
import { entityPageUrl, isLinkEntityType, PORTFOLIO_LAB_REF } from "./navigation/openPageSpec";
import { linksByRef } from "./toolResultStore";

/**
 * Resolving a handle the model wrote into the link the client is holding for it.
 *
 * This is the sphere-ai half of the inline-handle mechanism; the generic half — the
 * remark plugin, the `sphere-handle:` scheme and the provider — lives in `@mdotm/shadcn`'s
 * `inline-refs.tsx`, which knows nothing about tools or MCP. Everything specific to how a
 * link GOT here is in this file and in toolResultStore.ts.
 *
 * The arrangement in one line: sphere-mcp withholds an address the model cannot safely
 * hold (a presigned URL it could only retype, and would retype wrong), hands it to the
 * client in the tool result's `structuredContent` under `links`, and gives the model a
 * short `ref` instead. The model writes an ordinary Markdown link pointing at that ref —
 * `[here](sphere-handle://doc-3f9a2c71)` — and this resolver supplies the address the
 * renderer swaps in. The link's words stay the model's; only its target is ours.
 *
 * A `rel: "page"` link is the one exception to "the address came from the server": it
 * names a Sphere entity (`entityType` + `entityId`) instead of carrying an `href`, because
 * the entity's URL depends on THIS viewer's own entitlements and has to be resolved here,
 * live, so the route guard is re-evaluated on every render — including a History reopen,
 * potentially in a different session, where a URL resolved back when the link arrived
 * could be wrong in either direction. Resolving to `undefined` is not an error: the remark
 * plugin already unwraps an unresolved handle to plain text, which is exactly "guard
 * failed → show the name, not a dead link".
 *
 * **Deliberately the ONLY way a tool link is shown.** No card under the message, no
 * suffix appended to it, no separate affordance anywhere — a link appears where the model
 * put it and nowhere else. Consulted-page citations look superficially similar and are
 * not this: they are derived from arguments the model already holds and rendered by
 * consultedPages.ts, which is a different mechanism with a different source.
 *
 * One ref is fixed rather than minted: {@link PORTFOLIO_LAB_REF}, the Portfolio Lab page, which the
 * model may write with no tool result behind it (openPageSpec.ts's `portfolioLabLinkRule`). It
 * resolves through the same guarded {@link entityPageUrl} as a minted Lab link.
 *
 * A link to the page the viewer is already on is not shown either: it would take them nowhere.
 * SphereAIThread wraps the resolver with {@link isCurrentPage} against the {@link pageKey} of the
 * live location, so the same answer shows plain text here and a link once they move, and back.
 * The .docx export keeps every link: the document is read away from any page.
 */

/**
 * Stable across renders, and required to be: it feeds a remark plugin whose identity
 * decides whether the assistant's markdown is re-parsed, so a fresh closure per render
 * would re-parse every message on every render.
 *
 * That stability is why the lookup reads through {@link linksByRef} on each call rather
 * than closing over an index. The store is a module-level map written off the transcript
 * and by the restore, so the freshest answer is the one read at call time — and a link that
 * arrives with the tool result is in place before the prose naming it has streamed.
 */
export function resolveToolLink(ref: string): string | undefined {
	// The Lab takes no id (openPageSpec.ts's ENTITY_PAGE), so the one passed here is never read.
	if (ref === PORTFOLIO_LAB_REF) return entityPageUrl("portfolio_lab", PORTFOLIO_LAB_REF);
	const link = linksByRef().get(ref);
	if (!link) return undefined;
	if (link.entityType !== undefined && link.entityId !== undefined) {
		return isLinkEntityType(link.entityType) ? entityPageUrl(link.entityType, link.entityId, link.asOf) : undefined;
	}
	return link.href;
}

/**
 * The VIEW of a page that its query picks, as PortfolioDetails derives it: a valid past as-of day,
 * else the proposal when `proposal` is `"true"` and no as-of day is set, else the live page. Every
 * other param (a tab, say) is the viewer's own state on the same page, and does not make a link to
 * it worth showing.
 */
function viewOf(search: URLSearchParams): string {
	const rawAsOf = search.get(AS_OF_SEARCH_PARAM);
	const asOf = parseAsOfParam(rawAsOf);
	if (asOf) return `${AS_OF_SEARCH_PARAM}=${asOf}`;
	return search.get(PROPOSAL_SEARCH_PARAM) === "true" && rawAsOf == null ? PROPOSAL_SEARCH_PARAM : "live";
}

/**
 * The path a page link would use for the page at `pathname`, without a trailing slash. A portfolio
 * opened from a workspace sits at `PortfolioDetailsWithWorkspace`, which shows the same portfolio
 * page as the `PortfolioDetails` path a link names; every other path is its own.
 */
function linkPathOf(pathname: string): string {
	let path = pathname;
	while (path.length > 1 && path.endsWith("/")) path = path.slice(0, -1);
	const inWorkspace = matchPath<{ portfolioUid: string }>(path, {
		path: routesMap.PortfolioDetailsWithWorkspace.baseUrl,
		exact: true,
	});
	return inWorkspace ? typedUrlForRoute("PortfolioDetails", { portfolioUid: inWorkspace.params.portfolioUid }) : path;
}

/** The page a location shows, as a key: two locations with the same key show the same page and view. */
export function pageKey(pathname: string, search: string): string {
	return `${linkPathOf(pathname)} ${viewOf(new URLSearchParams(search))}`;
}

/**
 * Whether a resolved link points at the page whose {@link pageKey} is `currentPage`. An absolute
 * download URL is on another origin, and a URL that does not parse goes nowhere, so neither matches.
 */
export function isCurrentPage(url: string, currentPage: string): boolean {
	let target: URL;
	try {
		target = new URL(url, window.location.origin);
	} catch {
		return false;
	}
	return target.origin === window.location.origin && pageKey(target.pathname, target.search) === currentPage;
}
